// src/app/features/wallet/walletApi.js
import http from "../../api/http.js";

// ==================== OVERVIEW ====================

export async function getWalletOverview() {
  const res = await http.get("/wallet/overview");
  const balances = Array.isArray(res?.data?.balances) ? res.data.balances : [];
  // ✅ Add totalUsdValue from backend if available
  const totalUsdValue = res?.data?.totalUsdValue || null;
  return { balances, totalUsdValue };
}

// ==================== DEPOSIT ADDRESS ====================

export async function getDepositAddress(asset, network) {
  const a = String(asset || "").toUpperCase();
  const n = String(network || "MAINNET").toUpperCase();

  const res = await http.get(`/wallet/deposit-address/${encodeURIComponent(a)}`, {
    params: { network: n },
  });

  const address = res?.data?.address || "";
  const memo = res?.data?.memo || null;
  const qrPayload = res?.data?.qrPayload || (memo ? `${address}?memo=${memo}` : address);

  return {
    asset: res?.data?.asset || a,
    network: res?.data?.network || n,
    address,
    memo,
    qrPayload,
  };
}

// ==================== WITHDRAWALS ====================

function parseAmountStrict(v) {
  const raw = String(v ?? "").trim().replace(/,/g, "");
  const num = Number(raw);
  if (!Number.isFinite(num) || num <= 0) {
    return null;
  }
  return num;
}

export async function requestWithdraw(withdrawalData) {
  const amount = parseAmountStrict(withdrawalData.amount);

  const toAddress = String(
    withdrawalData.toAddress || withdrawalData.address || ""
  ).trim();

  const payload = {
    asset: String(withdrawalData.asset || "").toUpperCase(),
    network: String(withdrawalData.network || "MAINNET").toUpperCase(),
    amount,
    toAddress,
    memo: withdrawalData.memo ? String(withdrawalData.memo) : null,
  };

  if (!payload.asset) throw new Error("Asset is required");
  if (!payload.network) throw new Error("Network is required");
  if (payload.amount == null) throw new Error("Enter a valid withdrawal amount");
  if (!payload.toAddress || payload.toAddress.length < 10) throw new Error("Enter a valid destination address");

  const res = await http.post("/wallet/withdraw", payload);
  return res.data;
}

// ==================== TRANSFERS ====================

export async function transferFunds(transferData) {
  const payload = {
    asset: String(transferData.asset || "").toUpperCase(),
    network: String(transferData.network || "MAINNET").toUpperCase(),
    amount: Number(transferData.amount),
    toAccountId: String(transferData.toAccountId || ""),
    note: transferData.note || undefined,
  };

  const res = await http.post("/wallet/transfer", payload);
  return res.data;
}

// ==================== TRANSACTIONS ====================

export async function listWalletTransactions(params = {}) {
  const limit = Number(params.limit ?? 50);
  const offset = Number(params.offset ?? 0);

  const res = await http.get("/wallet/transactions", {
    params: { limit, offset },
  });

  const items = Array.isArray(res?.data?.items) ? res.data.items : [];
  return {
    items,
    total: Number(res?.data?.total ?? items.length),
    limit: Number(res?.data?.limit ?? limit),
    offset: Number(res?.data?.offset ?? offset),
  };
}

// ==================== PORTFOLIO VALUE ====================

/**
 * Get portfolio value with real-time market prices
 * @param {Array} balances - Array of balances from wallet overview
 * @param {Object} marketPrices - Market prices from marketApi
 * @returns {Object} - Total portfolio value and breakdown
 */
export function calculatePortfolioValue(balances, marketPrices) {
  if (!balances || !balances.length || !marketPrices) {
    return { total: 0, breakdown: [] };
  }

  let total = 0;
  const breakdown = [];

  for (const balance of balances) {
    const asset = balance.asset;
    const available = Number(balance.available || 0);
    const locked = Number(balance.locked || 0);
    const amount = available + locked;

    if (amount === 0) continue;

    let priceInUsdt = 0;
    let source = 'unknown';

    // USDT is the base currency
    if (asset === "USDT") {
      priceInUsdt = 1;
      source = 'base';
    } 
    // Try to get price from market data
    else if (marketPrices[asset]) {
      // If the asset is directly available (e.g., BTC, ETH)
      if (marketPrices[asset].usdt) {
        priceInUsdt = Number(marketPrices[asset].usdt);
        source = marketPrices[asset].source || 'market';
      } 
      // If asset is in the format "BTCUSDT"
      else if (marketPrices[`${asset}USDT`]) {
        const priceData = marketPrices[`${asset}USDT`];
        priceInUsdt = typeof priceData === 'number' ? priceData : Number(priceData.price || 0);
        source = typeof priceData === 'number' ? 'market' : (priceData.source || 'market');
      }
    } 
    // Try to get price from market prices object
    else if (marketPrices.prices && marketPrices.prices[asset]) {
      priceInUsdt = Number(marketPrices.prices[asset] || 0);
      source = 'market';
    }
    // Try with asset + USDT
    else if (marketPrices.prices && marketPrices.prices[`${asset}USDT`]) {
      priceInUsdt = Number(marketPrices.prices[`${asset}USDT`] || 0);
      source = 'market';
    }

    if (priceInUsdt > 0) {
      const valueInUsdt = amount * priceInUsdt;
      total += valueInUsdt;
      breakdown.push({
        asset,
        amount,
        available,
        locked,
        priceInUsdt,
        valueInUsdt,
        source
      });
    }
  }

  return { total, breakdown };
}

export default {
  getWalletOverview,
  getDepositAddress,
  listWalletTransactions,
  requestWithdraw,
  transferFunds,
  calculatePortfolioValue,
};
